/**
 * InfinitSales - Inbox Page (Simplified)
 * WhatsApp-like interface with SSE real-time updates
 * Business logic moved to backend
 * 
 * UPDATED v3:
 * - Added SyncingModal for first-time connection sync
 * - Improved connection flow: QR Code -> Syncing -> Inbox
 * - NEW: Temporary polling after first connection (15s x 5 times)
 *   to handle Evolution API sync delay
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { channelOf, isOpaqueChannel, isOpaqueKey, openChatState, routeOf } from '../../utils/channels';
import { findChatByPhone } from '../../utils/phoneSearch';
import { isEmbedded, navMobileBottom, navMobileTop } from '../../utils/embed';
import InboxBrand from '../../components/brand/InboxBrand';
import { normalizeCreatePhone } from '../../components/contacts/createPhoneRule';
import { WifiOff, X, Loader2, UserCheck } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useSSE, useSSEEvent, SSE_EVENTS, SSE_STATES } from '../../contexts/SSEContext';

import whatsappService from '../../services/wa-personal/whatsappService';
import chatUtils from '../../utils/wa-personal/chat_utils';
import { isDelegated, getDelegation } from '../../utils/delegation';
import ConnectionModal from '../../components/wa-personal/ConnectionModal';
import SyncingModal from '../../components/wa-personal/SyncingModal';
import DelegatedDisconnectedState from '../../components/empresa/DelegatedDisconnectedState';

import authService from '../../services/global/authService';
import subscriptionService from '../../services/global/subscriptionService';
import { getChatState, getChatStatesBatch, resumeAgent } from '../../services/agents/agentService';
import NavSidebar from '../../components/NavSidebar';
import NotificationBell from '../../components/notifications/NotificationBell';
import PromoModal from '../../components/PromoModal';
import AgentActivationModal from '../../components/AgentActivationModal';
import useAgentTrainingNotification from '../../hooks/useAgentTrainingNotification';
import CreateCompanyNotification from '../../components/notifications/CreateCompanyNotification';
import useCreateCompanyNotification from '../../hooks/useCreateCompanyNotification';
import ContactTransferredNotification from '../../components/notifications/ContactTransferredNotification';
import useContactTransferredNotifications from '../../hooks/useContactTransferredNotifications';
import SwipeToast from '../../components/notifications/SwipeToast';
import ChatList from './ChatList';
import ChatWindow from './ChatWindow';
// Frentes C1, C2 e C4 (flags persistent_layout, inbox_store, chat_cache).
import { useInboxLayout } from '../../components/inbox/InboxLayout';
import { useChannelChats, setInboxScrollTopSilent } from '../../services/inbox/inboxStore';
import { getBootFlag } from '../../hooks/useFeatureFlags';
import {
  appendChatMessage, prefetchChatMessages, createHoverPrefetcher,
} from '../../services/inbox/chatCache';
import { loadWaPersonalMessages } from '../../services/inbox/chatMessageLoaders';

import LabelsPage from '../global/LabelsPage';
import TagsKanbanPage from '../global/TagsKanbanPage';
import AgentsPage from '../global/AgentsPage';
import FollowUpsPage from '../global/FollowUpsPage';
// Polling configuration for first connection sync
const SYNC_POLLING_INTERVAL = 15000; // 15 seconds
const SYNC_POLLING_MAX_ATTEMPTS = 5;

// Casa telefones tolerando código de país (55) e o 9º dígito BR. Canonicaliza
// para DDD + número (sem 55, sem o 9 do celular) e compara. Usado no deep-link
// da notificação de contato transferido (o telefone pode vir com/sem o 9).
const _digitsNoCC = (raw) => {
  let d = String(raw || '').replace(/\D/g, '');
  if (d.length > 11 && d.startsWith('55')) d = d.slice(2);  // tira código de país
  return d;
};
const _canonPhone = (raw) => {
  let d = _digitsNoCC(raw);
  if (d.length === 11 && d[2] === '9') d = d.slice(0, 2) + d.slice(3);  // tira o 9
  return d;
};
const _phoneMatches = (a, b) => {
  const da = _digitsNoCC(a);
  const db = _digitsNoCC(b);
  if (!da || !db) return false;
  if (da === db) return true;  // match exato (não colapsa o 9)
  // Só colapsa o 9º dígito quando os COMPRIMENTOS diferem (11 vs 10) — evita
  // casar um fixo de 10 díg com um celular diferente de mesmo sufixo.
  if (da.length !== db.length) return _canonPhone(a) === _canonPhone(b);
  return false;
};

const Inbox = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { theme, toggleTheme, colors } = useTheme();

  // Workspace delegado: a conexão pertence ao dono do workspace — nunca abrir
  // modal/popup de conexão aqui. Estável: delegação só muda com reload completo.
  // CRM embutido em painel parceiro (2026-10-22): como na delegada, a conexão
  // (QR) é do dono e se faz na InfinitSales, não dentro do iframe.
  const delegated = isDelegated() || isEmbedded();

  // SSE context
  const { isConnected: sseConnected, sseState, reconnectInfo, reconnect: reconnectSSE } = useSSE();

  // Frente C1: com persistent_layout a NavSidebar mora no InboxLayout e este
  // shell so publica as props dela (ver efeito perto do render).
  const { layoutOwnsNav, setNavProps } = useInboxLayout();
  // Frente C4: cache de conversas abertas + prefetch no hover (decidido na
  // montagem, como o store da lista).
  const chatCacheOnRef = useRef(null);
  if (chatCacheOnRef.current === null) chatCacheOnRef.current = getBootFlag('chat_cache');
  const chatCacheOn = chatCacheOnRef.current;

  // Navigation state
  const [currentPage, setCurrentPage] = useState(location.state?.targetPage || 'inbox');

  useEffect(() => {
    if (location.state?.targetPage) {
      setCurrentPage(location.state.targetPage);
    }
    // Vindo de fora do shell (ex.: "Conexão WhatsApp" no inbox do Instagram):
    // o modal de conexão do canal mora aqui, então a intenção viaja por state.
    if (location.state?.openConnection && !isDelegated()) {
      setShowConnectionModal(true);
    }
    // Deep-link para uma conversa especifica (ex.: clicar numa notificacao de
    // handoff no painel do sino). Acha o chat por telefone/remoteJid e seleciona.
    // Deep-link que sobrevive a um reload (entrar no workspace do responsável
    // pelo Painel Comercial troca a sessão inteira, então location.state se
    // perde). O painel deixa o pedido no sessionStorage e nós consumimos aqui.
    let pendingOpenChat = null;
    try {
      const raw = sessionStorage.getItem('pendingOpenChat');
      if (raw) {
        pendingOpenChat = JSON.parse(raw);
        sessionStorage.removeItem('pendingOpenChat');
      }
    } catch (_e) { /* storage indisponível */ }
    const openChat = location.state?.openChat || pendingOpenChat;
    if (openChat) {
      setCurrentPage('inbox');
      const phone = openChat.contact_phone;
      const findAndSelect = (list) => {
        const t = (list || []).find(
          (c) => (openChat.chat_id && c.remoteJid === openChat.chat_id)
            || _phoneMatches(c._phoneNumber || (c.remoteJid || '').split('@')[0], phone)
        );
        if (t) { setSelectedChat(t); return true; }
        return false;
      };
      if (!findAndSelect(chatsRef.current)) {
        (async () => {
          // Usa a lista RETORNADA (chatsRef só atualiza no próximo commit).
          const reloaded = await loadChats(true);
          if (findAndSelect(reloaded)) return;
          // Fallback: busca server-side acha o contato fora da página 1 (ainda
          // é meu, só ranqueado abaixo do topo). Se vazio, saiu do workspace.
          try {
            const res = await whatsappService.searchChats(phone);
            if (findAndSelect(res?.data || [])) return;
          } catch (_e) { /* ignore */ }
          setOwnershipToast({
            description: 'Este contato foi transferido e não está mais no seu inbox.',
          });
        })();
      }
    }
    if (location.state?.targetPage || location.state?.openChat || location.state?.openConnection) {
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, location.pathname, navigate]);

  // Chat state. Frente C2: com inbox_store a lista vive no inboxStore e
  // sobrevive a troca de inbox (a volta pinta o cache e revalida por baixo);
  // sem a flag e o useState de sempre, devolvido pelo mesmo hook.
  const listState = useChannelChats('wa_personal');
  const { chats, setChats } = listState;
  const listStateRef = useRef(listState);
  listStateRef.current = listState;
  // Decisao de boot congelada na montagem: o cache serve para a primeira
  // pintura (ja houve fetch sem filtro) e precisa de revalidacao (> 15 s).
  const listBootRef = useRef({ cacheUsable: listState.cacheUsable, stale: listState.stale });
  const [selectedChat, setSelectedChat] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState({ status: 'checking', connected: false });
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  // Aviso de conexão-fantasma (403): dispensável na sessão.
  const [warnDismissed, setWarnDismissed] = useState(false);
  // "Reconectar" dispensado neste dispositivo (localStorage, por usuário): uma
  // vez fechado, NÃO reabre sozinho — só pelo menu (handleOpenConnection).
  // Importante p/ membro sem número (inbox virtual), cuja instância waiting_qr
  // fica reemitindo QR via SSE e reabrindo o modal/toast.
  const connectDismissedRef = useRef(false);
  const hasVirtualRef = useRef(false);
  const dismissConnect = useCallback(() => {
    connectDismissedRef.current = true;
    try { if (user?.id) localStorage.setItem(`infinitsales:wa_connect_dismissed:${user.id}`, '1'); } catch { /* ignore */ }
  }, [user?.id]);
  const clearConnectDismiss = useCallback(() => {
    connectDismissedRef.current = false;
    try { if (user?.id) localStorage.removeItem(`infinitsales:wa_connect_dismissed:${user.id}`); } catch { /* ignore */ }
  }, [user?.id]);
  useEffect(() => {
    try { connectDismissedRef.current = user?.id ? localStorage.getItem(`infinitsales:wa_connect_dismissed:${user.id}`) === '1' : false; } catch { /* ignore */ }
  }, [user?.id]);
  const [showSyncingModal, setShowSyncingModal] = useState(false);
  // Sem skeleton quando a lista ja esta no store (frente C2).
  const [loadingChats, setLoadingChats] = useState(!listBootRef.current.cacheUsable);
  const [showSidebar, setShowSidebar] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showDisconnectPopup, setShowDisconnectPopup] = useState(true);
  const [isFirstConnection, setIsFirstConnection] = useState(false);

  // Promo modal state (post-onboarding)
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [promoPlans, setPromoPlans] = useState([]);

  // Agent activation modal state (post-onboarding)
  const [showAgentActivation, setShowAgentActivation] = useState(false);
  const [activationLoading, setActivationLoading] = useState(false);

  // Toast de treinamento de agente REMOVIDO (aparecia constantemente p/ o
  // cliente). Mantemos só `trainingToastVisible` porque a notificação de criar
  // empresa depende dele (só aparece quando o treinamento não está pendente).
  const {
    visible: trainingToastVisible,
  } = useAgentTrainingNotification();
  const [autoOpenAgentTrain, setAutoOpenAgentTrain] = useState(false);

  // Create-company notification — Business tier sem empresa
  const {
    visible: createCompanyVisible,
    dismiss: dismissCreateCompany,
    markCreated: markCompanyCreated,
  } = useCreateCompanyNotification();

  const handleCreateCompanyClick = useCallback(() => {
    markCompanyCreated();
    navigate('/account?tab=billing&open_company_form=1');
  }, [markCompanyCreated, navigate]);


  // Autopilot state
  const [autopilotContacts, setAutopilotContacts] = useState([]);
  const [autopilotMode, setAutopilotMode] = useState('off');
  const [blockedContacts, setBlockedContacts] = useState([]);
  const [autopilotSince, setAutopilotSince] = useState(null); // DIV-31: carimbo "so novos"
  const [activeAgentName, setActiveAgentName] = useState(null);
  // Pausa do agente por canal (2026-10-14): este canal esta na lista do workspace.
  const [agentChannelsPaused, setAgentChannelsPaused] = useState([]);

  // Handoff state: { [phone]: { state, state_metadata } }
  const [handoffStates, setHandoffStates] = useState({});
  // Chaves em handoff para a lista, memoizadas: `Object.keys` inline criava um
  // array novo a cada render e invalidava o itemData das linhas do ChatList.
  const handoffChatKeys = useMemo(() => Object.keys(handoffStates), [handoffStates]);

  // Toast queue for handoff transferred events
  const transferredNotifications = useContactTransferredNotifications();

  // Aviso de propriedade: contato deste workspace transferido para outro
  // responsável (motor de propriedade). { description } ou null.
  const [ownershipToast, setOwnershipToast] = useState(null);

  // Refs
  const selectedChatRef = useRef(selectedChat);
  const syncPollingRef = useRef(null);
  const syncPollingAttemptsRef = useRef(0);
  const chatsRef = useRef(chats);

  // Keep refs in sync
  useEffect(() => {
    selectedChatRef.current = selectedChat;
  }, [selectedChat]);

  useEffect(() => {
    chatsRef.current = chats;
  }, [chats]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      stopSyncPolling();
    };
  }, []);

  // Post-onboarding: check for plan selection or show promo modal
  useEffect(() => {
    const justCompleted = localStorage.getItem('infinitsales-onboarding-just-completed');
    if (justCompleted !== 'true') return;

    localStorage.removeItem('infinitsales-onboarding-just-completed');

    const raw = localStorage.getItem('infinitsales-selected-plan');
    localStorage.removeItem('infinitsales-selected-plan');

    if (raw) {
      try {
        const { planId, timestamp } = JSON.parse(raw);
        const MAX_AGE_MS = 24 * 60 * 60 * 1000;
        if (planId && (Date.now() - timestamp) < MAX_AGE_MS) {
          (async () => {
            try {
              const result = await subscriptionService.getCheckoutUrl(planId);
              const checkoutUrl = result.data?.checkout_url || result.checkout_url;
              if (checkoutUrl) {
                window.location.href = checkoutUrl;
                return;
              }
            } catch (err) {
              console.error('[Inbox] Error getting checkout URL:', err);
            }
            loadPromoPlans();
          })();
          return;
        }
      } catch (e) {
        console.error('[Inbox] Error parsing selected plan:', e);
      }
    }

    loadPromoPlans();

    async function loadPromoPlans() {
      try {
        // Guard: only show promo modal for free users (tier=null)
        try {
          const status = await subscriptionService.getStatus();
          const tier = status?.data?.tier ?? status?.tier;
          if (tier) return;
        } catch (statusErr) {
          console.error('[Inbox] Error checking subscription status:', statusErr);
        }
        const res = await subscriptionService.getPlans();
        const data = res.data || res;
        const paid = Array.isArray(data) ? data.filter(p => !p.is_combo && p.price_brl > 0) : [];
        setPromoPlans(paid);
        setShowPromoModal(true);
      } catch (err) {
        console.error('[Inbox] Error loading plans for promo:', err);
      }
    }
  }, []);

  // Load autopilot config
  const loadAutopilotConfig = useCallback(async () => {
    try {
      const response = await authService.getAutopilotConfig();
      if (response?.data) {
        setAutopilotContacts(response.data.autopilot_contacts || []);
        setAutopilotMode(response.data.autopilot_mode || 'off');
        setBlockedContacts(response.data.blocked_contacts || []);
        if ('autopilot_since' in (response.data || {})) setAutopilotSince(response.data.autopilot_since || null);
        // Agente por canal (22/10): o selo mostra quem responde NESTE inbox.
        setActiveAgentName(response.data.agent_names_by_channel?.wa_personal || response.data.active_agent_name || null);
        setAgentChannelsPaused(response.data.agent_channels_paused || []);
      }
    } catch (err) {
      console.error('[Inbox] Error loading autopilot config:', err);
    }
  }, []);

  // Carrega o autopilot-config quando o inbox está visível (cobre a montagem,
  // já que currentPage começa em 'inbox', E o retorno da tela de agentes).
  // Antes havia um 2º efeito "on mount" que duplicava a chamada na montagem.
  useEffect(() => {
    if (currentPage === 'inbox') {
      loadAutopilotConfig();
    }
  }, [currentPage, loadAutopilotConfig]);

  // Handler: toggle autopilot for a specific contact
  const handleToggleAutopilot = useCallback(async (phone, enabled) => {
    try {
      const response = await authService.toggleAutopilotContact(phone, enabled);
      if (response?.data?.autopilot_contacts) {
        setAutopilotContacts(response.data.autopilot_contacts);
      }
    } catch (err) {
      console.error('[Inbox] Error toggling autopilot:', err);
    }
  }, []);

  // Handler: toggle blocked contact (exclude from auto-respond when mode is "all")
  const handleToggleBlockedContact = useCallback(async (phone, blocked) => {
    try {
      const response = await authService.toggleBlockedContact(phone, blocked);
      if (response?.data?.blocked_contacts) {
        setBlockedContacts(response.data.blocked_contacts);
        if ('autopilot_since' in (response.data || {})) setAutopilotSince(response.data.autopilot_since || null);
      }
    } catch (err) {
      console.error('[Inbox] Error toggling blocked contact:', err);
    }
  }, []);

  // ============================================
  // SYNC POLLING (for first connection)
  // ============================================

  /**
   * Stop the sync polling
   */
  const stopSyncPolling = useCallback(() => {
    if (syncPollingRef.current) {
      console.log('[Inbox] Stopping sync polling');
      clearInterval(syncPollingRef.current);
      syncPollingRef.current = null;
      syncPollingAttemptsRef.current = 0;
    }
  }, []);

  /**
   * Start temporary polling for first connection
   * Polls every 15s, max 5 times, stops if chats are found
   */
  const startSyncPolling = useCallback(() => {
    // Don't start if already polling
    if (syncPollingRef.current) {
      console.log('[Inbox] Sync polling already active');
      return;
    }

    console.log('[Inbox] Starting sync polling (15s x 5 attempts)');
    syncPollingAttemptsRef.current = 0;

    syncPollingRef.current = setInterval(async () => {
      syncPollingAttemptsRef.current += 1;
      const attempt = syncPollingAttemptsRef.current;

      console.log(`[Inbox] Sync polling attempt ${attempt}/${SYNC_POLLING_MAX_ATTEMPTS}`);

      try {
        const response = await whatsappService.getChats();
        const newChats = response.data || [];

        console.log(`[Inbox] Sync polling got ${newChats.length} chats`);

        if (newChats.length > 0) {
          // Found chats! Update state and stop polling
          console.log('[Inbox] Chats found, stopping sync polling');
          setChats(newChats);
          listStateRef.current.markFetched({ page: 1, hasMore: Boolean(response.has_more), filters: null });
          stopSyncPolling();
          return;
        }
      } catch (err) {
        console.error('[Inbox] Sync polling error:', err);
      }

      // Check if we reached max attempts
      if (attempt >= SYNC_POLLING_MAX_ATTEMPTS) {
        console.log('[Inbox] Max sync polling attempts reached, stopping');
        stopSyncPolling();
      }
    }, SYNC_POLLING_INTERVAL);
  }, [stopSyncPolling, setChats]);

  // ============================================
  // SSE EVENT HANDLERS
  // ============================================

  // Handle CONNECTION_UPDATE from SSE
  useSSEEvent(SSE_EVENTS.CONNECTION_UPDATE, (data) => {
    console.log('[Inbox] SSE CONNECTION_UPDATE:', data);
    
    const wasDisconnected = !connectionStatus.connected;
    
    setConnectionStatus({
      status: data.status,
      connected: data.connected,
      phoneNumber: data.phone_number || data.phoneNumber,
    });

    // If just connected and was previously disconnected
    if (data.connected) {
      // Conectou de fato → zera o "dispensado" para um futuro disconnect voltar
      // a avisar normalmente.
      clearConnectDismiss();
    }

    if (data.connected && wasDisconnected) {
      console.log('[Inbox] Connection established, checking if first connection...');

      // Check if this is first connection (no chats loaded yet)
      if (chatsRef.current.length === 0 || isFirstConnection) {
        console.log('[Inbox] First connection, showing syncing modal...');
        setShowConnectionModal(false);
        setShowSyncingModal(true);
        loadChatsWithSync();
      } else {
        loadChats(false);
      }
    }
    
    if (!data.connected) {
      // Não reabre o toast se: inbox virtual (membro sem número) OU já foi
      // dispensado neste dispositivo. O reconectar manual segue no menu.
      if (!delegated && !hasVirtualRef.current && !connectDismissedRef.current) {
        setShowDisconnectPopup(true);
      }
      // Stop polling if disconnected
      stopSyncPolling();
    }
  }, [connectionStatus.connected, isFirstConnection, stopSyncPolling, delegated, clearConnectDismiss]);

  // Handle QRCODE_UPDATED from SSE
  useSSEEvent(SSE_EVENTS.QRCODE_UPDATED, (data) => {
    console.log('[Inbox] SSE QRCODE_UPDATED');
    // Preserva has_virtual_inbox (não sobrescreve o objeto inteiro) — senão o
    // membro sem número perderia o flag e o guard abaixo falharia.
    setConnectionStatus((prev) => ({ ...prev, status: 'waiting_qr', connected: false }));
    // Delegado nunca vê o QR. Inbox virtual (membro sem número) nunca abre o
    // QR. E, uma vez dispensado neste dispositivo, não reabre sozinho (só pelo
    // menu).
    if (delegated || hasVirtualRef.current || connectDismissedRef.current) return;
    if (!showConnectionModal) {
      setShowConnectionModal(true);
    }
  }, [showConnectionModal, delegated]);

  // Handle NEW_MESSAGE from SSE
  useSSEEvent(SSE_EVENTS.NEW_MESSAGE, (data) => {
    // Filtro de canal: este handler é o único que não olhava `source`. Uma
    // mensagem de outro canal (Instagram, wa_business) matava o sync polling
    // que popula a lista logo após conectar o WhatsApp por QR, deixando o
    // inbox vazio até um refresh manual. Payloads sem `source` são do Evolution
    // (formato legado) e continuam passando.
    if (data?.source && data.source !== 'wa_personal') return;

    console.log('[Inbox] SSE NEW_MESSAGE:', data?.remoteJid);

    // If we receive a message, chats are synced - stop polling
    if (syncPollingRef.current) {
      console.log('[Inbox] Received message, stopping sync polling');
      stopSyncPolling();
    }

    if (data) {
      updateChatWithNewMessage(data);
    }
  }, [stopSyncPolling]);

  // Handle CHAT_UPDATE from SSE
  useSSEEvent(SSE_EVENTS.CHAT_UPDATE, (data) => {
    console.log('[Inbox] SSE CHAT_UPDATE:', data?.action);

    // If we receive chat update, chats are synced - stop polling
    if (syncPollingRef.current) {
      console.log('[Inbox] Received chat update, stopping sync polling');
      stopSyncPolling();
    }

    // Debounced: na carga inicial (CHATS_SET/CONTACTS_SET) chegam rajadas e
    // cada evento recarregava a lista inteira.
    scheduleChatsReload();
  }, [stopSyncPolling]);

  // Handle CHATS_SYNC from SSE (when backend finishes syncing)
  useSSEEvent(SSE_EVENTS.CHATS_SYNC, (data) => {
    console.log('[Inbox] SSE CHATS_SYNC:', data);
    // Reload chats when sync completes
    stopSyncPolling();
    loadChats(true);
    setShowSyncingModal(false);
  }, [stopSyncPolling]);

  // Handle CONTACT_UPDATE from SSE (backend enriches name/picture from local cache)
  // Fired when the Evolution returns pushName="" or missing picture but our
  // wa_personal_contacts cache has a valid value — we patch the chat in place
  // so the header/title/avatar update without a full re-fetch.
  useSSEEvent(SSE_EVENTS.CONTACT_UPDATE, (data) => {
    if (!data) return;
    // "enriched" is our action when the backend fills a missing pushName/pic
    // from the local Cosmos cache. Other actions (upsert/update) just trigger
    // a chat list reload.
    // DIV-24 (2026-10-20): `renamed` (gaveta, tool do agente, API) atualiza o nome ao vivo.
    if ((data.action === 'enriched' || data.action === 'renamed') && data.number) {
      const phone = String(data.number);
      const patch = {};
      if (data.pushName || data.name) patch.pushName = data.pushName || data.name;
      if (data.profilePicUrl) patch.profilePicUrl = data.profilePicUrl;
      if (Object.keys(patch).length === 0) return;

      setChats(prevChats => prevChats.map(c => {
        const matches = c._phoneNumber === phone
          || chatUtils.extractPhone(c.remoteJid) === phone
          || (c.remoteJid || '').startsWith(`${phone}@`)
          || (c._altJid || '').startsWith(`${phone}@`);
        return matches ? { ...c, ...patch } : c;
      }));

      setSelectedChat(prev => {
        if (!prev) return prev;
        const matches = prev._phoneNumber === phone
          || chatUtils.extractPhone(prev.remoteJid) === phone
          || (prev.remoteJid || '').startsWith(`${phone}@`)
          || (prev._altJid || '').startsWith(`${phone}@`);
        return matches ? { ...prev, ...patch } : prev;
      });
    } else if (data.action === 'upsert' || data.action === 'update') {
      // Generic signal — refresh debounced (CONTACTS_SET chega em rajada).
      scheduleChatsReload();
    }
  }, []);

  // Handle chat_state_changed from SSE (handoff detection)
  useSSEEvent(SSE_EVENTS.NEW_MESSAGE, (data) => {
    if (data?.source !== 'wa_personal' || data?.type !== 'chat_state_changed') return;
    const phone = data.contact_phone;
    if (!phone) return;
    console.log('[Inbox] Chat state changed:', phone, data.state);
    if (data.state === 'handoff') {
      setHandoffStates(prev => ({
        ...prev,
        [phone]: { state: 'handoff', state_metadata: data.state_metadata || {} }
      }));
    } else {
      setHandoffStates(prev => {
        const next = { ...prev };
        delete next[phone];
        return next;
      });
    }
  }, []);

  // Handle CONTACT_TRANSFERRED — fired immediately when the agent transfers
  // (vs. chat_state_changed which only fires on the next inbound message).
  useSSEEvent(SSE_EVENTS.CONTACT_TRANSFERRED, (data) => {
    if (data?.source !== 'wa_personal') return;
    const phone = data.contact_phone;
    if (!phone) return;
    console.log('[Inbox] Contact transferred:', phone, data.reason);
    setHandoffStates(prev => ({
      ...prev,
      [phone]: { state: 'handoff', state_metadata: data.state_metadata || {} }
    }));
    transferredNotifications.enqueue(data);
  }, [transferredNotifications]);

  // Handle CONTACT_OWNERSHIP_CHANGED — motor de propriedade: o contato mudou
  // de dono entre workspaces. Quem perdeu a visão remove o chat da lista (e
  // fecha a conversa aberta, com aviso); quem recebeu recarrega a lista — o
  // cartão semeado aparece no próximo fetch. Contas sem company nunca recebem
  // este evento, então nada muda fora do fluxo de empresa.
  useSSEEvent(SSE_EVENTS.CONTACT_OWNERSHIP_CHANGED, (data) => {
    const myId = user?.id;
    if (!data?.phone || !myId) return;
    // Payload traz os canais afetados; este inbox só reage ao wa_personal.
    if (Array.isArray(data.channels) && data.channels.length > 0
        && !data.channels.includes('wa_personal')) return;
    const phoneDigits = String(data.phone).replace(/\D/g, '');
    if (!phoneDigits) return;
    const matchesPhone = (c) => {
      const digits = String(
        c?._phoneNumber || chatUtils.extractPhone(c?.remoteJid) || ''
      ).replace(/\D/g, '');
      return !!digits && digits === phoneDigits;
    };

    // hidden_for/visible_for cobrem multi-hop (transferência reversa): listam
    // TODAS as partições afetadas, não só prev/new/receiver do doc principal.
    // Fallback p/ o shape antigo quando o backend não envia as listas.
    const hiddenForMe = Array.isArray(data.hidden_for) && data.hidden_for.includes(myId);
    const visibleForMe = Array.isArray(data.visible_for) && data.visible_for.includes(myId);
    const lostLegacy = data.prev_owner === myId && data.new_owner !== myId;
    const receivedLegacy = data.receiver === myId || data.new_owner === myId;

    if (hiddenForMe || (lostLegacy && !visibleForMe)) {
      // Perdi a visão do contato: sai da lista e fecha o chat se era o aberto.
      console.log('[Inbox] Ownership perdido:', phoneDigits);
      setChats(prev => prev.filter(c => !matchesPhone(c)));
      setSelectedChat(prev => (prev && matchesPhone(prev) ? null : prev));
      const newOwnerName = data.assigned_to?.name;
      setOwnershipToast({
        description: newOwnerName
          ? `Novo responsável: ${newOwnerName}.`
          : 'Este contato agora é atendido por outro responsável.',
      });
    } else if (visibleForMe || receivedLegacy) {
      // Recebi/restaurei o contato: refaz o fetch p/ o cartão semeado aparecer.
      console.log('[Inbox] Ownership recebido:', phoneDigits);
      loadChats(false);
    }
  }, [user?.id]);

  // Agente ligado/desligado por outro membro do mesmo workspace: sincroniza o
  // estado do autopilot para o toggle refletir na hora em todas as telas.
  useSSEEvent(SSE_EVENTS.AUTOPILOT_CHANGED, (data) => {
    if (!data) return;
    if (Array.isArray(data.autopilot_contacts)) {
      setAutopilotContacts(data.autopilot_contacts);
    }
    if (data.autopilot_mode) {
      setAutopilotMode(data.autopilot_mode);
    }
    if ('autopilot_since' in data) setAutopilotSince(data.autopilot_since || null);
    if (Array.isArray(data.agent_channels_paused)) {
      setAgentChannelsPaused(data.agent_channels_paused);
    }
  }, []);

  // Agente retomado por outro membro: limpa o handoff (banner + tag) na hora.
  useSSEEvent(SSE_EVENTS.AGENT_RESUMED, (data) => {
    const phone = data?.contact_phone;
    if (!phone) return;
    setHandoffStates(prev => {
      const next = { ...prev };
      delete next[phone];
      return next;
    });
  }, []);

  // Telefones cobertos pelo ultimo lote POST /chats/state-batch. Frente C4:
  // ao abrir uma conversa que o lote ja cobriu, o estado de handoff e o que o
  // shell guarda em handoffStates (mantido ao vivo pelo SSE), sem o GET
  // /chats/{fone}/state por clique.
  const batchCoveredRef = useRef(new Set());

  // Load handoff state when selecting a chat
  const checkChatHandoffState = useCallback(async (phone) => {
    if (!phone) return;
    if (chatCacheOn && batchCoveredRef.current.has(phone)) return;
    try {
      const result = await getChatState(phone);
      if (result?.state === 'handoff') {
        setHandoffStates(prev => ({
          ...prev,
          [phone]: { state: 'handoff', state_metadata: result.state_metadata || {} }
        }));
      } else {
        setHandoffStates(prev => {
          const next = { ...prev };
          delete next[phone];
          return next;
        });
      }
    } catch (err) {
      console.error('[Inbox] Error checking handoff state:', err);
    }
  }, [chatCacheOn]);

  // Handler: resume agent for a handoff chat
  const handleResumeAgent = useCallback(async (phone) => {
    try {
      await resumeAgent(phone);
      setHandoffStates(prev => {
        const next = { ...prev };
        delete next[phone];
        return next;
      });
    } catch (err) {
      console.error('[Inbox] Error resuming agent:', err);
    }
  }, []);

  // Pré-carrega o estado de handoff de todos os chats em UMA única request
  // batch. Substitui o loop de N getChatState que travava o backend.
  useEffect(() => {
    if (loadingChats || chats.length === 0) return;
    let cancelled = false;
    (async () => {
      const phones = chats
        .map(c => (c._phoneNumber || chatUtils.extractPhone?.(c.remoteJid) || '').trim())
        .filter(Boolean);
      if (phones.length === 0) return;
      try {
        const states = await getChatStatesBatch(phones);
        if (cancelled) return;
        batchCoveredRef.current = new Set(phones);
        const handoffMap = {};
        Object.entries(states).forEach(([phone, state]) => {
          if (state?.state === 'handoff') {
            handoffMap[phone] = {
              state: 'handoff',
              state_metadata: state.state_metadata || {},
            };
          }
        });
        if (Object.keys(handoffMap).length > 0) {
          setHandoffStates(prev => ({ ...prev, ...handoffMap }));
        }
      } catch (err) {
        console.warn('[Inbox] handoff batch fetch failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [loadingChats, chats.length]);

  // ============================================
  // DATA LOADING
  // ============================================

  const checkConnection = useCallback(async () => {
    try {
      const response = await whatsappService.getConnectionStatus();
      const data = response.data || { status: 'disconnected', connected: false };
      setConnectionStatus(data);
      hasVirtualRef.current = !!data.has_virtual_inbox;
      return data;
    } catch (err) {
      console.error('Error checking connection:', err);
      const data = { status: 'error', connected: false };
      setConnectionStatus(data);
      return data;
    }
  }, []);

  // Pagination state — first page on load, "Carregar mais" appends.
  // 30 mantém o batch de handoff-state pequeno (1 round de paralelismo no
  // backend) e prioriza naturalmente os contatos mais recentes, já que o
  // backend ordena por updatedAt desc antes de paginar.
  const PAGE_SIZE = 30;
  // hasMore vive junto da lista (store ou useState, conforme a flag).
  const { hasMore: hasMoreChats, setHasMore: setHasMoreChats } = listState;
  const [loadingMoreChats, setLoadingMoreChats] = useState(false);
  // Filtro de etiqueta/funil ativo (ids). Ref (não state) p/ não recriar
  // loadChats nem disparar o efeito de init; a ChatList notifica via
  // onLabelFilterChange e o fetch passa a respeitar o funil — paginado.
  const labelFilterRef = useRef([]);
  // Filtros extras (responsável/data/sem-resposta) — mesma estratégia do
  // labelFilterRef: ref (não state) p/ não recriar loadChats; a ChatList
  // notifica via onExtraFiltersChange e o fetch server-side passa a respeitar.
  const extraFilterRef = useRef(null);

  // Há algum filtro server-side ligado agora? Com filtro ativo a lista `chats`
  // NÃO é "as conversas mais recentes", é "o resultado do filtro" — e nada pode
  // entrar nela sem passar pelo servidor.
  const filtrosAtivos = useCallback(() => {
    const e = extraFilterRef.current;
    return (labelFilterRef.current || []).length > 0
      || Boolean(e && (e.member_id || e.since || e.created_from || e.created_to || e.unanswered || e.unread
        || (e.custom_fields || []).length > 0));
  }, []);

  // Busca DENTRO dos filtros (INBOX_SEARCH_WITHIN_FILTERS_ENABLED): termo da
  // lupa informado pela ChatList. So vai ao servidor (`q`) com filtro ativo;
  // sem filtro, a busca global da ChatList segue como antes.
  const searchRef = useRef({ q: '', searchMessages: false });
  const extraComBusca = useCallback(() => {
    const s = searchRef.current;
    if (!s.q || !filtrosAtivos()) return extraFilterRef.current;
    return { ...(extraFilterRef.current || {}), q: s.q, search_messages: s.searchMessages };
  }, [filtrosAtivos]);

  const loadChats = useCallback(async (silent = false) => {
    if (!silent) setLoadingChats(true);
    try {
      const response = await whatsappService.getChats(PAGE_SIZE, 0, labelFilterRef.current, extraComBusca());
      const newChats = response.data || [];
      setChats(newChats);
      setHasMoreChats(Boolean(response.has_more));
      listStateRef.current.markFetched({
        page: 1,
        hasMore: Boolean(response.has_more),
        filters: { labelIds: labelFilterRef.current, extra: extraComBusca() },
      });

      // If we got chats, stop any active polling
      if (newChats.length > 0 && syncPollingRef.current) {
        stopSyncPolling();
      }
      return newChats;  // retorna a lista para quem precisa dela no mesmo tick
                        // (deep-link de notificação) sem depender do chatsRef.
    } catch (err) {
      console.error('Error loading chats:', err);
      if (!silent) setChats([]);
      return [];
    } finally {
      if (!silent) setLoadingChats(false);
    }
  }, [stopSyncPolling, setChats, setHasMoreChats, extraComBusca]);

  // Reload com debounce (trailing) para os sinais genéricos de SSE. Os
  // handlers chamam via closure — resolvido na hora do evento, então a
  // declaração depois deles não é problema.
  const reloadDebounceRef = useRef(null);
  const scheduleChatsReload = useCallback(() => {
    if (reloadDebounceRef.current) clearTimeout(reloadDebounceRef.current);
    reloadDebounceRef.current = setTimeout(() => {
      reloadDebounceRef.current = null;
      loadChats(true);
    }, 1200);
  }, [loadChats]);

  useEffect(() => () => {
    if (reloadDebounceRef.current) clearTimeout(reloadDebounceRef.current);
  }, []);

  const loadMoreChats = useCallback(async () => {
    if (loadingMoreChats || !hasMoreChats) return;
    setLoadingMoreChats(true);
    try {
      const response = await whatsappService.getChats(PAGE_SIZE, chats.length, labelFilterRef.current, extraComBusca());
      const more = response.data || [];
      setChats(prev => {
        // Dedupe by remoteJid in case of overlap.
        const seen = new Set(prev.map(c => c.remoteJid));
        return [...prev, ...more.filter(c => !seen.has(c.remoteJid))];
      });
      setHasMoreChats(Boolean(response.has_more));
      listStateRef.current.markFetched({
        page: (listStateRef.current.page || 1) + 1,
        hasMore: Boolean(response.has_more),
      });
    } catch (err) {
      console.error('Error loading more chats:', err);
    } finally {
      setLoadingMoreChats(false);
    }
  }, [chats.length, hasMoreChats, loadingMoreChats, setChats, setHasMoreChats, extraComBusca]);

  // Recebe a seleção de etiquetas da ChatList, guarda no ref e recarrega a
  // 1ª página server-side — o filtro por funil "puxa tudo" paginado, não só
  // o que já estava carregado na tela.
  const handleLabelFilterChange = useCallback((labelIds) => {
    labelFilterRef.current = Array.isArray(labelIds) ? labelIds.filter(Boolean) : [];
    loadChats(false);
  }, [loadChats]);

  // Filtros extras (responsável/data/sem-resposta) → refetch server-side.
  const handleExtraFilterChange = useCallback((extra) => {
    const hasAny = extra && (extra.member_id || extra.since || extra.created_from || extra.created_to || extra.unanswered || extra.unread
      || (extra.custom_fields || []).length > 0);
    extraFilterRef.current = hasAny ? extra : null;
    loadChats(false);
  }, [loadChats]);

  // Termo da lupa (debounced na ChatList). Com filtro ativo, muda a lista.
  const handleSearchTermChange = useCallback((q, searchMessages) => {
    const prev = searchRef.current;
    const next = { q: q || '', searchMessages: Boolean(searchMessages) };
    searchRef.current = next;
    if (filtrosAtivos() && (prev.q !== next.q || prev.searchMessages !== next.searchMessages)) {
      loadChats(false);
    }
  }, [filtrosAtivos, loadChats]);

  /**
   * Load chats with syncing modal
   * Shows syncing modal while loading, then hides it
   * If chats are empty after loading, starts temporary polling
   */
  const loadChatsWithSync = useCallback(async () => {
    setShowSyncingModal(true);
    setLoadingChats(true);
    
    try {
      // Small delay to show syncing animation
      await new Promise(resolve => setTimeout(resolve, 1500));

      const response = await whatsappService.getChats(PAGE_SIZE, 0, labelFilterRef.current, extraComBusca());
      const newChats = response.data || [];
      setChats(newChats);
      setHasMoreChats(Boolean(response.has_more));
      listStateRef.current.markFetched({
        page: 1,
        hasMore: Boolean(response.has_more),
        filters: { labelIds: labelFilterRef.current, extra: extraComBusca() },
      });

      console.log(`[Inbox] loadChatsWithSync got ${newChats.length} chats`);
      
      // Keep syncing modal for a bit longer on first connection
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // If no chats were loaded, start polling
      // Evolution API might still be syncing
      if (newChats.length === 0) {
        console.log('[Inbox] No chats loaded, starting sync polling...');
        startSyncPolling();
      }
      
    } catch (err) {
      console.error('Error loading chats during sync:', err);
      setChats([]);
      // Start polling even on error
      startSyncPolling();
    } finally {
      setLoadingChats(false);
      setShowSyncingModal(false);
      setIsFirstConnection(false);
    }
  }, [startSyncPolling, setChats, setHasMoreChats, extraComBusca]);

  // ============================================
  // CHAT UPDATE LOGIC (Simplified)
  // ============================================

  const updateChatWithNewMessage = useCallback((messageData) => {
    const { remoteJid, rawRemoteJid, pushName, textContent, timestamp, fromMe, key, messageType, hasMedia, serverTimestamp } = messageData;

    if (!remoteJid) return;

    // FILTRO ATIVO: conversa que ainda não está na lista não pode entrar por
    // aqui. Com filtro ligado a lista é o RESULTADO do filtro, e o card
    // fabricado abaixo furava esse resultado — aparecia no topo, sem nome e
    // sem etiqueta, parecendo que o contato filtrado tinha mandado mensagem
    // (relato do cliente, 25/08). Quem decide se ele entra é o servidor:
    // recarrega a 1ª página COM os filtros, agrupando a rajada.
    //
    // Decidido FORA do updater de propósito: o React pode invocar o updater
    // mais de uma vez (StrictMode) e disparar fetch lá dentro geraria
    // requisição duplicada — mesma armadilha já documentada no Instagram.
    const jaNaLista = chatUtils.findChatIndex(
      chatsRef.current || [], remoteJid, key?.remoteJidAlt, rawRemoteJid,
    ) >= 0;
    if (!jaNaLista && filtrosAtivos()) {
      scheduleChatsReload();
      return;
    }

    // Frente C4: conversa em cache mas NAO aberta recebe a mensagem como
    // patch (append com dedup), em vez de perder a entrada. A conversa aberta
    // e do ChatWindow, que espelha o proprio estado no cache.
    if (chatCacheOn && !fromMe) {
      const aberta = selectedChatRef.current
        && chatUtils.isSameChat(selectedChatRef.current, { remoteJid, _phoneNumber: chatUtils.extractPhone(remoteJid) });
      if (!aberta) {
        const msg = messageData.messagePayload
          ? { ...messageData.messagePayload, id: messageData.messagePayload.id || messageData.messageId || `sse-${Date.now()}` }
          : {
            id: messageData.messageId || `sse-${Date.now()}`,
            key: key || { id: messageData.messageId, fromMe, remoteJid },
            message: { conversation: textContent },
            messageType,
            messageTimestamp: timestamp || Math.floor(Date.now() / 1000),
            pushName,
            status: 'received',
            sentByName: messageData.sentByName || null,
          };
        appendChatMessage('wa_personal', remoteJid, msg, (m) => m?.key?.id || m?.id);
      }
    }

    const altJid = key?.remoteJidAlt;
    // pushName de fromMe é "Você"/nome do dono; em grupo é o PARTICIPANTE que
    // falou — nenhum dos dois pode virar nome do chat.
    const safePushName = (!fromMe && !chatUtils.isGroup(remoteJid) && pushName) ? pushName : null;

    // serverTimestamp (ISO server-side) tem prioridade sobre o timestamp
    // do webhook Evolution (unix seconds, pode vir defasado em bursts). Em
    // ultima instancia, usa client now() para garantir que mensagens novas
    // sempre ganhem da ordem de chats stale.
    const updatedAtIso = serverTimestamp
      || (timestamp ? new Date(timestamp * 1000).toISOString() : new Date().toISOString());

    setChats(prevChats => {
      // Find chat using utility (casa também pela forma CRUA do jid — chats
      // @lid legados na lista vs jid canônico vindo do backend)
      const existingIndex = chatUtils.findChatIndex(prevChats, remoteJid, altJid, rawRemoteJid);

      if (existingIndex >= 0) {
        // Update existing chat
        const newChats = [...prevChats];
        const [existingChat] = newChats.splice(existingIndex, 1);

        const updatedChat = {
          ...existingChat,
          pushName: existingChat.pushName || safePushName,
          lastMessage: {
            ...existingChat.lastMessage,
            message: { conversation: textContent },
            key: key,
            messageTimestamp: timestamp,
            pushName: pushName,
          },
          updatedAt: updatedAtIso,
          unreadCount: fromMe ? (existingChat.unreadCount || 0) : (existingChat.unreadCount || 0) + 1,
        };

        // Re-sort para garantir ordem por data em rajadas.
        const merged = [updatedChat, ...newChats];
        merged.sort((a, b) => {
          const da = new Date(a.updatedAt || 0);
          const db = new Date(b.updatedAt || 0);
          return db - da;
        });
        return merged;
      } else {
        // Anti-ressurreição (motor de propriedade): chat oculto para o dono
        // NÃO volta para a lista quando chega mensagem nova. Chats que JÁ
        // estão na lista continuam atualizando normalmente (branch acima).
        if (messageData.hidden_for_owner === true) {
          return prevChats;
        }
        // New chat. NUNCA usar o número cru como pushName — era a causa do
        // "número sem formatação" no chat list (pushName é o nível 1 da
        // cadeia de exibição e pula o formatador).
        const newChat = {
          id: `new-${Date.now()}`,
          remoteJid,
          pushName: safePushName,
          lastMessage: {
            message: { conversation: textContent },
            key: key,
            messageTimestamp: timestamp,
            pushName: pushName,
          },
          updatedAt: timestamp ? new Date(timestamp * 1000).toISOString() : new Date().toISOString(),
          unreadCount: fromMe ? 0 : 1,
          _phoneNumber: chatUtils.extractPhone(remoteJid),
          _altJid: altJid,
        };

        return [newChat, ...prevChats];
      }
    });
  }, [filtrosAtivos, scheduleChatsReload, chatCacheOn, setChats]);

  // ============================================
  // INITIAL LOAD
  // ============================================

  useEffect(() => {
    const init = async () => {
      // PARALELO: a lista não espera mais o /status (que faz um round-trip
      // ao vivo à Evolution — até 30s no pior caso e centenas de ms no
      // normal). O fetch de chats é ESPECULATIVO com erro engolido: o
      // backend cobre o caso virtual (usa a instância virtual quando não há
      // número) e devolve vazio/erro quando desconectado de verdade — aí a
      // decisão de modal/empty-state continua 100% guiada pelo status.
      // Frente C2: lista ja no store pinta na hora; revalida em silencio se
      // tem mais de 15 s, e nem isso se ainda esta fresca.
      const boot = listBootRef.current;
      const chatsPromise = boot.cacheUsable && !boot.stale
        ? Promise.resolve()
        : loadChats(boot.cacheUsable).catch(() => {});
      const data = await checkConnection();
      if (data.connected || data.has_virtual_inbox) {
        await chatsPromise;
      } else if (delegated) {
        // Workspace delegado desconectado: sem modal de conexão — o
        // empty-state (renderPageContent) cobre o aviso.
        await chatsPromise;
        setLoadingChats(false);
      } else {
        // Mark as first connection attempt
        setIsFirstConnection(true);
        // Não abre o modal de conexão se já foi dispensado neste dispositivo
        // (reconectar segue disponível pelo menu).
        if (!connectDismissedRef.current) setShowConnectionModal(true);
        await chatsPromise;
        setLoadingChats(false);
      }
    };
    init();
  }, [checkConnection, loadChats, delegated]);

  // ============================================
  // EVENT HANDLERS
  // ============================================

  /**
   * Called when ConnectionModal reports successful connection
   * This now triggers the syncing flow
   */
  const handleConnectionSuccess = async () => {
    console.log('[Inbox] handleConnectionSuccess called');
    setShowConnectionModal(false);
    clearConnectDismiss();

    // Show syncing modal for first-time connection
    if (isFirstConnection || chatsRef.current.length === 0) {
      await loadChatsWithSync();
    } else {
      await checkConnection();
      await loadChats(false);
    }
  };

  // Nova conversa pelo botão + da busca: abre um ChatWindow vazio para o número
  // (o Evolution cria a conversa de fato no 1º envio; a lista atualiza via SSE).
  const handleStartNewChat = (phone, name) => {
    // O drawer "Criar contato" já entrega a chave normalizada (só dígitos).
    // Normalizar de novo somaria 55 a um número estrangeiro de 11 dígitos
    // declarado com "+" (Chile 56912345678), então só texto cru passa pela regra.
    const raw = String(phone || '').trim();
    const digits = /^\d+$/.test(raw) ? raw : normalizeCreatePhone(raw);
    if (!digits) return;
    // Conversa já carregada com este número (com ou sem 55 e 9º dígito): abre
    // ela em vez de uma conversa vazia paralela (auditoria de 25/09/2026).
    const existing = findChatByPhone(
      (chatsRef.current || []).filter((c) => !(c._isGroup || chatUtils.isGroup(c.remoteJid))),
      digits,
      (c) => c._phoneNumber || c.remoteJid,
    );
    if (existing) {
      if (!(selectedChat && chatUtils.isSameChat(selectedChat, existing))) handleSelectChat(existing);
      return;
    }
    setSelectedChat({
      remoteJid: `${digits}@s.whatsapp.net`,
      _phoneNumber: digits,
      _isNew: true,
      pushName: name || null,
      name: name || null,
      profilePicUrl: null,
    });
  };

  const handleSelectChat = (chat) => {
    // Toggle: clicar no chat ja selecionado faz deselecionar (limpa painel)
    if (selectedChat && chatUtils.isSameChat(selectedChat, chat)) {
      setSelectedChat(null);
      return;
    }

    setSelectedChat(chat);

    // Clear unread count
    setChats(prevChats =>
      prevChats.map(c =>
        chatUtils.isSameChat(c, chat) ? { ...c, unreadCount: 0 } : c
      )
    );

    if (isMobile) setShowSidebar(false);

    // Check handoff state for this chat
    const phone = chat._phoneNumber || chatUtils.extractPhone?.(chat.remoteJid) || '';
    if (phone) checkChatHandoffState(phone);
  };

  const handleBackToChats = () => {
    setSelectedChat(null);
    setShowSidebar(true);
  };

  // Frente C4: passar o mouse na linha (desktop, debounce 150 ms) ja busca a
  // primeira pagina de mensagens para o cache; o clique pinta na hora.
  const hoverPrefetcher = useMemo(() => createHoverPrefetcher(150), []);
  useEffect(() => () => hoverPrefetcher.cancel(), [hoverPrefetcher]);
  const handleHoverChat = useCallback((chat) => {
    const jid = chat?.remoteJid;
    if (!jid || chat?._isNew) return;
    // A conversa aberta e do ChatWindow (que ja buscou): nao duplica o GET.
    if (selectedChatRef.current && chatUtils.isSameChat(selectedChatRef.current, chat)) return;
    hoverPrefetcher.schedule(() => {
      prefetchChatMessages('wa_personal', jid, () => loadWaPersonalMessages(jid));
    });
  }, [hoverPrefetcher]);
  // Posicao da lista guardada sem re-render (so leitura na proxima montagem).
  const handleListScroll = useCallback((offset) => {
    setInboxScrollTopSilent('wa_personal', offset);
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await checkConnection();
    if (connectionStatus.connected) {
      await loadChats(false);
    }
    setIsRefreshing(false);
  };

  const handleMessageSent = (messageData) => {
    if (selectedChat && messageData) {
      updateChatWithNewMessage({
        remoteJid: selectedChat.remoteJid,
        textContent: messageData.text,
        timestamp: Math.floor(Date.now() / 1000),
        fromMe: true,
        key: { remoteJid: selectedChat.remoteJid, fromMe: true },
      });
    }
  };

  const handlePageChange = (page) => {
    if (page === 'meta-leads') {
      navigate('/meta-leads');
      return;
    }
    if (page === 'saved-messages') {
      navigate('/saved-messages');
      return;
    }
    if (page === 'campaigns') {
      navigate('/campaigns');
      return;
    }
    setCurrentPage(page);
  };
  const handleOpenConnection = () => {
    // Guarda defensiva: em workspace delegado não há ação de conectar.
    if (isDelegated()) return;
    setShowConnectionModal(true);
  };

  // ============================================
  // STYLES
  // ============================================

  const styles = {
    container: {
      display: 'flex',
      width: '100%',
      // Mobile: 100dvh + overflow hidden + overscroll-behavior none segura a
      // tela (sem rubber-band do <body>) SEM usar position:fixed — o fixed +
      // teclado do iOS travava a interface ao sair do foco do chat. Inputs
      // >=16px já evitam o zoom ao focar.
      ...(isMobile
        ? { height: '100dvh', overscrollBehavior: 'none' }
        : { height: '100vh', width: '100vw' }),
      // Frente C1: dentro do InboxLayout o shell e uma coluna flex ao lado da
      // NavSidebar do layout, nao a tela inteira.
      ...(layoutOwnsNav && !isMobile ? { width: 'auto', flex: 1, minWidth: 0 } : {}),
      overflow: 'hidden',
      backgroundColor: colors.bgSecondary,
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    mainArea: { flex: 1, display: 'flex', overflow: 'hidden' },
    inboxLayout: { flex: 1, display: 'flex', overflow: 'hidden' },
    sidebar: {
      width: isMobile ? '100%' : '395px',
      minWidth: isMobile ? '100%' : '395px',
      maxWidth: isMobile ? '100%' : '395px',
      // 100dvh no mobile acompanha a barra do navegador (address bar) que
      // encolhe/expande; reserva espaço para a barra inferior (bottom tabs).
      height: isMobile ? '100dvh' : '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: colors.bgPrimary,
      borderRight: isMobile ? 'none' : `1px solid ${colors.borderColor}`,
      position: 'relative',
      // Mobile: reserva espaço para a barra superior (54) e a inferior (58),
      // ambas fixas e renderizadas pela NavSidebar.
      paddingTop: isMobile ? navMobileTop() : 0,
      paddingBottom: isMobile ? navMobileBottom() : 0,
    },
    // Barra superior do inbox no DESKTOP: marca "InfinitSales" + sino. No mobile
    // esse papel é da barra superior fixa da NavSidebar.
    inboxBrandBar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 10px 0 16px',
      height: 58,
      flexShrink: 0,
      borderBottom: `1px solid ${colors.borderColor}`,
      backgroundColor: colors.bgPrimary,
    },
    inboxBrand: {
      fontSize: 20,
      fontWeight: 700,
      letterSpacing: '0.2px',
      color: colors.textPrimary,
      fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif",
    },
    // Logo padronizada (igual ao login): container branco circular. No tema
    // CLARO leva sombra/borda porque o fundo da logo é branco e some no claro.
    inboxBrandLogo: {
      width: 32, height: 32, borderRadius: 8,
      backgroundColor: '#ffffff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      overflow: 'hidden', flexShrink: 0,
      boxShadow: theme === 'dark' ? 'none' : '0 1px 4px rgba(0,0,0,0.18)',
      border: theme === 'dark' ? 'none' : `1px solid ${colors.borderColor}`,
    },
    chatArea: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: colors.bgChat,
      overflow: 'hidden',
      // No mobile a conversa abre em tela cheia (a bottom-bar some).
      height: isMobile ? '100dvh' : undefined,
    },
    mobileHeader: {
      display: isMobile ? 'flex' : 'none',
      alignItems: 'center',
      padding: '12px 16px',
      backgroundColor: colors.headerBg,
      borderBottom: `1px solid ${colors.borderColor}`,
    },
    mobileMenuBtn: {
      width: 40, height: 40,
      border: 'none', background: 'transparent',
      color: colors.textPrimary, cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    mobileTitle: {
      flex: 1, fontWeight: 500, fontSize: 18, textAlign: 'center', color: colors.textPrimary,
    },
    disconnectionPopup: {
      position: 'fixed', bottom: 60, left: '50%', transform: 'translateX(-50%)',
      backgroundColor: colors.bgPrimary, color: colors.textPrimary,
      padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12,
      zIndex: 90, borderRadius: 12, border: `1px solid ${colors.danger}`, boxShadow: colors.shadowLg,
    },
    disconnectionBtn: {
      padding: '6px 14px', backgroundColor: colors.danger, color: 'white',
      border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500,
    },
    banner: {
      position: 'fixed', top: 0, left: 0, right: 0,
      color: 'white', padding: '8px 16px',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
      fontSize: 14, zIndex: 100,
    },
  };

  // ============================================
  // RENDER
  // ============================================

  const renderPageContent = () => {
    switch (currentPage) {
      case 'followups': return <FollowUpsPage />;
      case 'labels': return <LabelsPage />;
      case 'tags-kanban': return (
        <TagsKanbanPage
          onOpenChat={(contact) => {
            // Cartao de Instagram ou Messenger (chave opaca, sem telefone):
            // abre o inbox DO CANAL pelo mesmo deep-link do sino/toast
            // (state.openChat), em vez de cair no inbox de WhatsApp.
            const ref = String(contact?.contact_ref || contact?.phone || '');
            // Canal opaco (Instagram, Messenger, widget): rota e campo do openChat
            // pelo mapa de canais, a partir do prefixo da chave (2026-09-23).
            if (isOpaqueKey(ref) || isOpaqueChannel(contact?.channel)) {
              const canal = isOpaqueKey(ref) ? channelOf(ref) : contact.channel;
              navigate(routeOf(canal), { state: { openChat: openChatState(canal, ref) } });
              return;
            }
            const normalized = (contact?.phone || '').replace(/\D/g, '');
            // Igual exato primeiro, depois com e sem 55 e 9º dígito: o card
            // 553491609492 abre a conversa 5534991609492 em vez de duplicar.
            const match = findChatByPhone(
              (chatsRef.current || []).filter((c) => !(c._isGroup || chatUtils.isGroup(c.remoteJid))),
              normalized,
              (c) => c._phoneNumber || c.remoteJid,
            );
            if (match) {
              handleSelectChat(match);
            } else if (normalized) {
              // Contato sem conversa previa (ex: importado via CSV). Cria
              // um pseudo-chat client-side com _isNew=true para o ChatWindow
              // mostrar input vazio e permitir enviar a primeira mensagem.
              // A Evolution API aceita qualquer numero — apos o envio o
              // webhook MESSAGES_UPSERT cria o chat real e substitui o
              // pseudo via lista atualizada por SSE.
              const pseudo = {
                _isNew: true,
                _phoneNumber: normalized,
                remoteJid: `${normalized}@s.whatsapp.net`,
                // null (e nao o numero cru): a cadeia de exibicao cai no
                // formatPhoneNumber em vez de mostrar digitos sem mascara.
                pushName: contact?.display_name || contact?.name || null,
                profilePicUrl: contact?.profile_pic_url || null,
                unreadCount: 0,
                lastMessageTimestamp: Math.floor(Date.now() / 1000),
                _lastMessagePreview: '',
              };
              setChats((prev) => {
                // evita duplicar se ja existir um pseudo do mesmo telefone
                const exists = prev.some((c) =>
                  (c._phoneNumber || '').replace(/\D/g, '') === normalized
                );
                return exists ? prev : [pseudo, ...prev];
              });
              handleSelectChat(pseudo);
            }
            setCurrentPage('inbox');
          }}
        />
      );
      case 'agents': return (
        <AgentsPage
          autoOpenTrainingFor={autoOpenAgentTrain}
          onAutoOpenConsumed={() => setAutoOpenAgentTrain(false)}
        />
      );
      case 'inbox':
      default:
        // Workspace delegado sem WhatsApp conectado: sem lista/conversa nem
        // ação de conectar — só o aviso com o caminho de volta aos workspaces.
        if (delegated && !connectionStatus.connected && connectionStatus.status !== 'checking') {
          return <DelegatedDisconnectedState workspaceName={getDelegation()?.targetName} />;
        }
        return (
          <div style={styles.inboxLayout}>
            {/* No mobile vira navegação tipo WhatsApp: ou a LISTA ocupa a tela,
                ou a CONVERSA ocupa a tela — nunca as duas (sem overlay/transform,
                o que matava o "toque que só dava foco"). No desktop, as duas
                aparecem lado a lado como antes. */}
            {(!isMobile || !selectedChat) && (
              <aside style={styles.sidebar}>
                {!isMobile && (
                  <div style={styles.inboxBrandBar}>
                    <InboxBrand logoStyle={styles.inboxBrandLogo} textStyle={styles.inboxBrand} />
                    <NotificationBell />
                  </div>
                )}
                <ChatList
                  chats={chats}
                  selectedChat={selectedChat}
                  onSelectChat={handleSelectChat}
                  onStartNewChat={handleStartNewChat}
                  loading={loadingChats}
                  hasMore={hasMoreChats}
                  onLoadMore={loadMoreChats}
                  loadingMore={loadingMoreChats}
                  autopilotContacts={autopilotContacts}
                  autopilotMode={autopilotMode}
                  autopilotSince={autopilotSince}
                  blockedContacts={blockedContacts}
                  channelPaused={agentChannelsPaused.includes('wa_personal')}
                  activeAgentName={activeAgentName}
                  handoffChats={handoffChatKeys}
                  onLabelFilterChange={handleLabelFilterChange}
                  onExtraFiltersChange={handleExtraFilterChange}
                  onSearchTermChange={handleSearchTermChange}
                  // Frentes C2 e C4: sem as flags, undefined e 0 (como hoje).
                  onHoverChat={chatCacheOn ? handleHoverChat : undefined}
                  initialScrollOffset={listState.fromStore ? listState.scrollTop : 0}
                  onScrollOffset={listState.fromStore ? handleListScroll : undefined}
                />
              </aside>
            )}

            {(!isMobile || selectedChat) && (
            <main style={styles.chatArea}>
              <ChatWindow
                chat={selectedChat}
                chats={chats}
                isMobile={isMobile}
                onBack={handleBackToChats}
                onMessageSent={handleMessageSent}
                autopilotContacts={autopilotContacts}
                autopilotMode={autopilotMode}
                  autopilotSince={autopilotSince}
                  blockedContacts={blockedContacts}
                  channelPaused={agentChannelsPaused.includes('wa_personal')}
                blockedContacts={blockedContacts}
                activeAgentName={activeAgentName}
                onToggleAutopilot={handleToggleAutopilot}
                onToggleBlockedContact={handleToggleBlockedContact}
                isHandoff={selectedChat ? !!handoffStates[selectedChat._phoneNumber || ''] : false}
                handoffMetadata={selectedChat ? handoffStates[selectedChat._phoneNumber || '']?.state_metadata : null}
                onResumeAgent={handleResumeAgent}
                onContactNameUpdated={({ remoteJid, phone, newName }) => {
                  // Reflete o nome editado no painel "Dados do contato" tanto
                  // na ChatList lateral quanto no header (selectedChat). O
                  // backend ja marca push_name_edited_by_user para impedir
                  // overwrite via webhook futuro.
                  setChats((prev) => prev.map((c) => {
                    if (c.remoteJid && c.remoteJid === remoteJid) {
                      return { ...c, pushName: newName };
                    }
                    if (c._phoneNumber && phone && c._phoneNumber === phone) {
                      return { ...c, pushName: newName };
                    }
                    return c;
                  }));
                  setSelectedChat((curr) => {
                    if (!curr) return curr;
                    if (curr.remoteJid === remoteJid) {
                      return { ...curr, pushName: newName };
                    }
                    if (curr._phoneNumber === phone) {
                      return { ...curr, pushName: newName };
                    }
                    return curr;
                  });
                }}
                onContactDeleted={(phone) => {
                  // Purga concluída: tira a conversa da lista e fecha o chat.
                  // Sem isto o contato continuava visível até dar F5, dando a
                  // impressão de que a exclusão não funcionou.
                  const digits = String(phone || '').replace(/\D/g, '');
                  setChats((prev) => prev.filter((c) => {
                    const cPhone = String(c._phoneNumber || '').replace(/\D/g, '');
                    return !(digits && cPhone === digits);
                  }));
                  setSelectedChat(null);
                }}
              />
            </main>
            )}
          </div>
        );
    }
  };

  // Frente C1: a NavSidebar do layout recebe as props deste shell. Sem deps
  // de proposito: os handlers sao recriados a cada render e o layout so
  // re-renderiza quando currentPage/isRefreshing/mobileHideNav mudam.
  const navProps = {
    currentPage,
    onPageChange: handlePageChange,
    onRefresh: handleRefresh,
    onOpenConnection: handleOpenConnection,
    isRefreshing,
    mobileHideNav: isMobile && currentPage === 'inbox' && !!selectedChat,
  };
  useEffect(() => {
    if (layoutOwnsNav) setNavProps(navProps);
  });

  return (
    <div style={styles.container}>
      {/* SSE Failed Banner */}
      {sseState === SSE_STATES.FAILED && (
        <div style={{ ...styles.banner, backgroundColor: '#ef4444', cursor: 'pointer' }} onClick={reconnectSSE}>
          <WifiOff size={16} />
          <span>Conexão perdida. Clique para reconectar.</span>
        </div>
      )}

      {/* Reconnecting Banner */}
      {reconnectInfo && (
        <div style={{ ...styles.banner, backgroundColor: colors.warning || '#f59e0b' }}>
          <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
          <span>Reconectando... ({reconnectInfo.attempt}/{reconnectInfo.maxAttempts})</span>
        </div>
      )}

      {/* Navigation Sidebar (no InboxLayout quando persistent_layout esta ON) */}
      {!layoutOwnsNav && (
        <NavSidebar
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onRefresh={handleRefresh}
          onOpenConnection={handleOpenConnection}
          isRefreshing={isRefreshing}
          mobileHideNav={isMobile && currentPage === 'inbox' && !!selectedChat}
        />
      )}

      {/* Main Area */}
      <div style={styles.mainArea}>
        {renderPageContent()}
      </div>

      {/* Connection Modal */}
      <ConnectionModal
        isOpen={showConnectionModal}
        onClose={() => { setShowConnectionModal(false); dismissConnect(); }}
        onConnected={handleConnectionSuccess}
      />

      {/* Syncing Modal */}
      <SyncingModal
        isOpen={showSyncingModal}
      />

      {/* Disconnection Popup */}
      {!delegated &&
       currentPage === 'inbox' &&
       !connectionStatus.connected &&
       !connectionStatus.has_virtual_inbox &&
       connectionStatus.status !== 'checking' &&
       !showConnectionModal &&
       !showSyncingModal &&
       showDisconnectPopup && (
        <div style={styles.disconnectionPopup}>
          <WifiOff size={18} style={{ color: colors.danger }} />
          <span style={{ fontSize: 14, color: colors.textSecondary }}>WhatsApp desconectado</span>
          <button style={styles.disconnectionBtn} onClick={() => setShowConnectionModal(true)}>
            Reconectar
          </button>
          <button
            style={{ padding: 4, background: 'transparent', border: 'none', color: colors.textTertiary, cursor: 'pointer' }}
            onClick={() => { setShowDisconnectPopup(false); dismissConnect(); }}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Aviso de conexão-fantasma (403): connectionStatus.connected pode estar
          true, mas o WhatsApp rejeitou a sessão e as mensagens não saem. Mostra
          mesmo "conectado" — o operador precisa saber para reconectar. */}
      {!delegated &&
       currentPage === 'inbox' &&
       connectionStatus.connection_warning &&
       !showConnectionModal &&
       !warnDismissed && (
        <div style={{ ...styles.disconnectionPopup, borderColor: '#f59e0b' }}>
          <WifiOff size={18} style={{ color: '#f59e0b' }} />
          <span style={{ fontSize: 13, color: colors.textSecondary, maxWidth: 320 }}>
            {connectionStatus.connection_warning.message
              || 'A conexão do WhatsApp pode estar com problema — as mensagens podem não estar sendo entregues. Reconecte o número.'}
          </span>
          <button style={styles.disconnectionBtn} onClick={() => { setWarnDismissed(true); setShowConnectionModal(true); }}>
            Reconectar
          </button>
          <button
            style={{ padding: 4, background: 'transparent', border: 'none', color: colors.textTertiary, cursor: 'pointer' }}
            onClick={() => setWarnDismissed(true)}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Post-onboarding Promo Modal */}
      <PromoModal
        isOpen={showPromoModal}
        onClose={() => {
          setShowPromoModal(false);
          // Show agent activation popup after promo closes (only once)
          const activationShown = localStorage.getItem('infinitsales-agent-activation-shown');
          if (!activationShown && user?.active_agent_id) {
            setShowAgentActivation(true);
            localStorage.setItem('infinitsales-agent-activation-shown', 'true');
          }
        }}
        plans={promoPlans}
      />

      {/* Toast de treinamento de agente removido (aparecia constantemente). */}

      {/* Create-company Notification — apparent only if training toast is gone
          and user has Business plan but no company yet. Agent training is the
          higher-priority pre-req so we wait for it to be dismissed/done. */}
      <CreateCompanyNotification
        isOpen={createCompanyVisible && !trainingToastVisible && currentPage === 'inbox' && !showPromoModal && !showAgentActivation}
        onCreate={handleCreateCompanyClick}
        onDismiss={dismissCreateCompany}
      />

      {/* Contact Transferred Notification — agregado via hook em janela de
          8s (UMA notif "N transferidos" em vez de N toasts sequenciais). */}
      <ContactTransferredNotification
        isOpen={!!transferredNotifications.current && !showPromoModal && !showAgentActivation}
        batch={transferredNotifications.current?.batch || []}
        onOpenChat={(payload) => {
          if (!payload) return;
          const phone = payload.contact_phone;
          setCurrentPage('inbox');
          const match = (c) => (payload.remote_jid && c.remoteJid === payload.remote_jid)
            || _phoneMatches(c._phoneNumber || (c.remoteJid || '').split('@')[0], phone);
          const target = (chatsRef.current || []).find(match);
          if (target) {
            setSelectedChat(target);
          } else {
            (async () => {
              const reloaded = await loadChats(true);
              let hit = (reloaded || []).find(match);
              if (!hit) {
                try {
                  const res = await whatsappService.searchChats(phone);
                  hit = (res?.data || []).find(match);
                } catch (_e) { /* ignore */ }
              }
              if (hit) setSelectedChat(hit);
              else setOwnershipToast({
                description: 'Este contato foi transferido e não está mais no seu inbox.',
              });
            })();
          }
          transferredNotifications.dismissCurrent();
        }}
        onDismiss={transferredNotifications.dismissCurrent}
      />

      {/* Aviso de propriedade — contato deste workspace transferido para
          outro responsável (o chat sai da lista na hora). */}
      <SwipeToast
        isOpen={!!ownershipToast && !showPromoModal && !showAgentActivation}
        icon={<UserCheck size={20} />}
        title="Contato transferido"
        description={ownershipToast?.description}
        onDismiss={() => setOwnershipToast(null)}
        accentColor="#2563EB"
      />

      {/* Post-onboarding Agent Activation Modal */}
      <AgentActivationModal
        isOpen={showAgentActivation}
        loading={activationLoading}
        onClose={() => setShowAgentActivation(false)}
        onActivateAll={async () => {
          setActivationLoading(true);
          try {
            await authService.updateAutopilotConfig('all');
            setAutopilotMode('all');
          } catch (err) {
            console.error('Error activating agent for all:', err);
          } finally {
            setActivationLoading(false);
            setShowAgentActivation(false);
          }
        }}
        onSetSchedule={() => {
          setShowAgentActivation(false);
          setCurrentPage('agents');
        }}
      />

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { overflow: hidden; }
      `}</style>
    </div>
  );
};

export default Inbox;